/**
 * Character Pick Node - Player View
 *
 * Champion select screen. Sequential pick order where players choose their
 * character/hero. Active pick slot pulses with energy. Already-picked
 * characters are dimmed/locked. Team rosters fill in with slide animations.
 * Uses shared PlayerCard, TeamHeader, and party color system.
 */

import type { FlowPlayer } from "@rivaled/shared";
import { AnimatePresence, motion } from "framer-motion";
import { Check, UserPlus } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { ErrorBanner } from "../../components/ErrorBanner";
import { NodeCard } from "../../components/NodeCard";
import { NodeHeader } from "../../components/NodeHeader";
import { PlayerAvatar } from "../../components/PlayerAvatar";
import { useCurrentPlayerId } from "../../hooks/useCurrentPlayerId";
import type { NodePlayerViewProps } from "../types";

type Character = {
  id: string;
  name: string;
  imageUrl?: string;
};

type CharacterPickState = {
  characterPool: Character[];
  pickedCharacters: Record<string, string>; // playerId -> characterId
  pickOrder: string[];
  currentPickIndex: number;
  bannedCharacters?: string[];
};

export function CharacterPickView({
  nodeState,
  players,
  onAction,
}: NodePlayerViewProps<CharacterPickState>) {
  const userId = useCurrentPlayerId();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const state = nodeState.state as CharacterPickState;
  const config = nodeState.config as { labels?: { title?: string } };
  const title = config.labels?.title ?? "Character Select";

  const currentPickerId = state.pickOrder[state.currentPickIndex];
  const isMyTurn = currentPickerId === userId;
  const myPick = state.pickedCharacters[userId || ""];
  const isComplete = state.currentPickIndex >= state.pickOrder.length;

  const getPlayer = (id: string) => players.find((p) => p.userId === id);
  const currentPicker = getPlayer(currentPickerId);

  const handlePick = async (characterId: string) => {
    if (!isMyTurn || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    const result = await onAction("pick", { characterId, userId });

    if (!result.success && result.error) {
      setError(result.error);
    }

    setIsSubmitting(false);
  };

  const isUnavailable = (charId: string) => {
    return (
      Object.values(state.pickedCharacters).includes(charId) ||
      (state.bannedCharacters?.includes(charId) ?? false)
    );
  };

  const getCharacter = (charId: string) => state.characterPool.find((c) => c.id === charId);

  return (
    <NodeCard>
      <NodeHeader
        title={title}
        subtitle={
          <AnimatePresence mode="wait">
            <motion.p
              key={isComplete ? "complete" : isMyTurn ? "your-turn" : `picker-${currentPickerId}`}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="text-sm @sm:text-base"
            >
              {isComplete ? (
                <span className="text-brand-400 font-semibold">All characters selected</span>
              ) : isMyTurn ? (
                <span className="text-brand-400 font-semibold">Your turn to pick!</span>
              ) : currentPicker ? (
                <span className="text-text-secondary">
                  Waiting for{" "}
                  <span className="text-text-primary font-medium">{currentPicker.username}</span> to
                  pick&hellip;
                </span>
              ) : (
                <span className="text-text-secondary">Waiting&hellip;</span>
              )}
            </motion.p>
          </AnimatePresence>
        }
      />

      {/* Progress pill */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-6"
      >
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-lg bg-surface-800/80 border border-surface-700/50">
          <UserPlus size={16} className="text-brand-400" aria-hidden="true" />
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {Math.min(state.currentPickIndex, state.pickOrder.length)}
          </span>
          <span className="text-surface-500 text-sm">/</span>
          <span className="font-display text-2xl font-bold text-text-primary tabular-nums">
            {state.pickOrder.length}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            picked
          </span>
          <div className="w-px h-5 bg-surface-700/60" />
          <span className="font-display text-lg font-bold text-brand-400 tabular-nums">
            {state.characterPool.filter((c) => !isUnavailable(c.id)).length}
          </span>
          <span className="text-xs uppercase tracking-wider text-text-secondary font-medium">
            available
          </span>
        </div>
      </motion.div>

      <ErrorBanner error={error} />

      {/* Your pick confirmation */}
      <AnimatePresence>
        {myPick && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="mb-6"
          >
            <div className="flex items-center justify-center gap-3 px-5 py-3 rounded-lg bg-brand-500/[0.08] border border-brand-500/20 max-w-sm mx-auto">
              <Check size={16} className="text-brand-400 shrink-0" aria-hidden="true" />
              <span className="text-sm text-text-secondary">
                You picked{" "}
                <span className="font-display font-semibold text-brand-400">
                  {getCharacter(myPick)?.name ?? myPick}
                </span>
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Character grid */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <span className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-wider text-text-secondary">
            Character Roster
          </span>
          <div className="flex-1 h-px bg-surface-700/40" />
        </div>

        <div
          className={cn(
            "grid gap-3 @md:gap-4",
            state.characterPool.length <= 6
              ? "grid-cols-2 @lg:grid-cols-3"
              : state.characterPool.length <= 9
                ? "grid-cols-3 @lg:grid-cols-3"
                : state.characterPool.length <= 16
                  ? "grid-cols-3 @3xl:grid-cols-4"
                  : "grid-cols-4 @xl:grid-cols-5",
          )}
        >
          {state.characterPool.map((char, index) => {
            const unavailable = isUnavailable(char.id);
            const pickedBy = Object.entries(state.pickedCharacters).find(
              ([, cId]) => cId === char.id,
            )?.[0];
            const pickedByPlayer = pickedBy ? (getPlayer(pickedBy) ?? null) : null;
            const isBanned = state.bannedCharacters?.includes(char.id);

            return (
              <CharacterPickCard
                key={char.id}
                character={char}
                unavailable={unavailable}
                pickedByPlayer={pickedByPlayer}
                isBanned={!!isBanned}
                isMyTurn={isMyTurn}
                disabled={isSubmitting || !isMyTurn || unavailable}
                index={index}
                onPick={handlePick}
              />
            );
          })}
        </div>
      </div>

      {/* Pick order timeline */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.4 }}
        className="mt-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="font-display text-xs @sm:text-sm font-semibold uppercase tracking-wider text-text-secondary">
            Pick Order
          </span>
          <div className="flex-1 h-px bg-surface-700/40" />
        </div>

        <div className="flex flex-wrap gap-2">
          {state.pickOrder.map((playerId, index) => {
            const player = getPlayer(playerId);
            const hasPicked = !!state.pickedCharacters[playerId];
            const isCurrent = index === state.currentPickIndex && !isComplete;
            const isMe = playerId === userId;
            const pickedChar = state.pickedCharacters[playerId];
            const charName = pickedChar ? getCharacter(pickedChar)?.name : null;

            return (
              <motion.div
                key={`${playerId}-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: Math.min(0.5 + index * 0.03, 0.8) }}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all duration-300",
                  isCurrent
                    ? "bg-brand-500/15 ring-2 ring-brand-500/40 shadow-[0_0_12px_rgba(38,217,181,0.08)]"
                    : hasPicked
                      ? "bg-surface-800/60 border border-surface-700/30"
                      : "bg-surface-800/40 border border-surface-700/20",
                )}
              >
                <span
                  className={cn(
                    "text-[10px] tabular-nums font-medium shrink-0",
                    isCurrent ? "text-brand-400" : "text-surface-500",
                  )}
                >
                  {index + 1}.
                </span>

                {player && (
                  <PlayerAvatar
                    avatarUrl={player.avatarUrl}
                    username={player.username}
                    size="sm"
                    ringColor={
                      isMe
                        ? "ring-brand-500/50"
                        : isCurrent
                          ? "ring-brand-400/30"
                          : "ring-surface-600/30"
                    }
                  />
                )}

                <span
                  className={cn(
                    "font-medium truncate max-w-[80px]",
                    isMe
                      ? "text-brand-300"
                      : isCurrent
                        ? "text-text-primary"
                        : "text-text-secondary",
                    hasPicked && !isCurrent && !isMe && "text-surface-400",
                  )}
                >
                  {player?.username ?? "Unknown"}
                </span>

                {hasPicked && charName && (
                  <span className="text-[10px] text-surface-500 truncate max-w-[60px] hidden @sm:inline">
                    {charName}
                  </span>
                )}

                {hasPicked && (
                  <Check size={12} className="text-brand-500/60 shrink-0" aria-hidden="true" />
                )}

                {isCurrent && (
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse shrink-0" />
                )}
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* Draft complete indicator */}
      {isComplete && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500/[0.07] border border-brand-500/20">
            <Check size={14} className="text-brand-400" aria-hidden="true" />
            <span className="text-sm text-text-secondary">
              Character select{" "}
              <span className="font-display font-semibold text-brand-400">complete</span>
            </span>
          </div>
        </motion.div>
      )}
    </NodeCard>
  );
}

// ---------------------------------------------------------------------------
// Character pick card component
// ---------------------------------------------------------------------------

type CharacterPickCardProps = {
  character: Character;
  unavailable: boolean;
  pickedByPlayer: FlowPlayer | null;
  isBanned: boolean;
  isMyTurn: boolean;
  disabled: boolean;
  index: number;
  onPick: (characterId: string) => void;
};

function CharacterPickCard({
  character,
  unavailable,
  pickedByPlayer,
  isBanned,
  isMyTurn,
  disabled,
  index,
  onPick,
}: CharacterPickCardProps) {
  const delay = Math.min(0.1 + index * 0.05, 0.5);

  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      whileHover={!disabled ? { scale: 1.03 } : undefined}
      whileTap={!disabled ? { scale: 0.97 } : undefined}
      onClick={() => onPick(character.id)}
      disabled={disabled}
      className={cn(
        "group relative rounded-lg overflow-hidden text-left",
        "transition-all duration-200",
        "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2 focus:ring-offset-surface-900",
        unavailable
          ? isBanned
            ? "ring-1 ring-red-500/20 cursor-default"
            : "ring-1 ring-surface-700/30 cursor-default"
          : isMyTurn
            ? "ring-1 ring-surface-700/50 hover:ring-brand-500/50 cursor-pointer"
            : "ring-1 ring-surface-700/50 cursor-not-allowed opacity-80",
      )}
    >
      {/* Character portrait */}
      <div className="relative aspect-[3/4] bg-surface-800 overflow-hidden">
        {character.imageUrl ? (
          <img
            src={character.imageUrl}
            alt={character.name}
            className={cn(
              "w-full h-full object-cover transition-all duration-300",
              unavailable
                ? "brightness-[0.3] saturate-0 grayscale"
                : isMyTurn
                  ? "brightness-75 saturate-90 group-hover:brightness-90 group-hover:saturate-100"
                  : "brightness-60 saturate-75",
            )}
          />
        ) : (
          <div
            className={cn(
              "w-full h-full flex items-center justify-center transition-colors duration-300",
              unavailable
                ? "bg-surface-800 text-surface-600"
                : "bg-surface-800 text-surface-500 group-hover:text-surface-400",
            )}
          >
            <span className="font-display text-3xl @sm:text-4xl font-bold">
              {character.name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Hover glow */}
        {!unavailable && isMyTurn && (
          <div className="absolute inset-0 bg-brand-500/0 group-hover:bg-brand-500/10 transition-colors duration-200" />
        )}

        {/* Character name */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 @sm:p-3">
          <p
            className={cn(
              "font-display font-bold text-sm @sm:text-base leading-tight truncate",
              unavailable ? "text-surface-400" : "text-white",
              isBanned && "line-through decoration-red-500/60 decoration-2",
            )}
            style={{ textShadow: unavailable ? "none" : "0 2px 6px rgba(0,0,0,0.7)" }}
          >
            {character.name}
          </p>
        </div>

        {/* Picked overlay */}
        <AnimatePresence>
          {pickedByPlayer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center bg-black/20"
            >
              {/* Checkmark badge */}
              <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-500/80 flex items-center justify-center">
                <Check size={10} strokeWidth={3} stroke="white" aria-hidden="true" />
              </div>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.05 }}
                className="flex flex-col items-center gap-1.5"
              >
                <PlayerAvatar
                  avatarUrl={pickedByPlayer.avatarUrl}
                  username={pickedByPlayer.username}
                  size="md"
                  ringColor="ring-brand-500/50"
                />
                <div className="px-2.5 py-0.5 bg-brand-500/80 backdrop-blur-sm rounded-sm border border-brand-400/30">
                  <span className="text-[10px] font-semibold text-white uppercase tracking-wider truncate max-w-[80px] block">
                    {pickedByPlayer.username}
                  </span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Banned overlay */}
        <AnimatePresence>
          {isBanned && !pickedByPlayer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex flex-col items-center justify-center"
            >
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/30 origin-top-left rotate-[30deg]"
                  style={{ top: "38%" }}
                />
                <div
                  className="absolute top-0 left-0 w-[150%] h-px bg-red-500/20 origin-top-left rotate-[30deg]"
                  style={{ top: "44%" }}
                />
              </div>
              <motion.div
                initial={{ scale: 0, rotate: -12 }}
                animate={{ scale: 1, rotate: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
              >
                <div className="px-3 py-1 bg-red-500/90 backdrop-blur-sm rounded-sm border border-red-400/30 shadow-[0_0_20px_rgba(239,68,68,0.3)]">
                  <span className="font-display font-bold text-white text-xs uppercase tracking-wider">
                    Banned
                  </span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top accent line for pickable cards */}
        {!unavailable && isMyTurn && (
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-500/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        )}
      </div>
    </motion.button>
  );
}
